import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const featuredPlaylistApi = createApi({
	tagTypes: ['featuredPlaylist'],
	reducerPath: 'featuredPlaylistApi',
	baseQuery: fetchBaseQuery({
		baseUrl: `${import.meta.env.VITE_APP_API_URL}/`,
		prepareHeaders: (headers, { getState }) => {
			if (getState()?.authState?.userToken) {
				headers.set('Authorization', 'Bearer ' + getState()?.authState?.userToken);
			}
			return headers;
		}
	}),
	endpoints: builder => ({
		featuredPlayList: builder.query({
			query: payload => ({
				url: 'featurevideo',
				headers: {
					Authorization: `Bearer ${payload.token}`
				}
			}),
			providesTags: ['featuredPlaylist']
		}),
		submitFeaturedPlayList: builder.mutation({
			query: payload => ({
				url: 'featurevideo/save',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['featuredPlaylist']
		}),
		featuredPlayListById: builder.query({
			query: data => ({
				url: `featurevideo/${data.playlist}/${data.id}`,
				method: 'GET'
			}),
			providesTags: ['featuredPlaylist']
		}),
		deleteFeaturedPlayList: builder.mutation({
			query: id => ({
				url: `featurevideo/delete/${id}`,
				method: 'DELETE'
			}),
			invalidatesTags: ['featuredPlaylist']
		})
	})
});
export const {
	useFeaturedPlayListQuery,
	useSubmitFeaturedPlayListMutation,
	useFeaturedPlayListByIdQuery,
	useDeleteFeaturedPlayListMutation
} = featuredPlaylistApi;

export const personalPlaylistApi = createApi({
	tagTypes: ['personalPlaylist'],
	reducerPath: 'personalPlaylistApi',
	baseQuery: fetchBaseQuery({
		baseUrl: `${import.meta.env.VITE_APP_API_URL}/`,
		prepareHeaders: (headers, { getState }) => {
			if (getState()?.authState?.userToken) {
				headers.set('Authorization', 'Bearer ' + getState()?.authState?.userToken);
			}
			return headers;
		}
	}),
	endpoints: builder => ({
		personalPlayList: builder.mutation({
			query: payload => ({
				url: 'personalvideo',
				method: 'POST',
				body: payload
			}),
			providesTags: ['personalPlaylist']
		}),
		getPersonalPlaylist: builder.query({
			query: payload => ({
				url: 'personalvideo/all',
				method: 'GET'
			}),
			providesTags: ['personalPlaylist']
		}),
		submitPersonalPlayList: builder.mutation({
			query: payload => ({
				url: 'personalvideo/save',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['personalPlaylist']
		}),
		personalPlayListById: builder.query({
			query: data => ({
				url: `personalvideo/${data.playlist}/${data.id}`,
				method: 'GET'
			}),
			providesTags: ['personalPlaylist']
		}),
		deletePersonalPlayList: builder.mutation({
			query: payload => ({
				url: `personalvideo/delete/${payload}`,
				method: 'DELETE'
			}),
			invalidatesTags: ['personalPlaylist']
		}),
		addToPlayList: builder.mutation({
			query: payload => ({
				url: 'personalvideo/playlist/add',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['personalPlaylist']
		})
	})
});

export const {
	usePersonalPlayListMutation,
	useGetPersonalPlayListQuery,
	useSubmitPersonalPlayListMutation,
	usePersonalPlayListByIdQuery,
	useDeletePersonalPlayListMutation,
	useAddToPlayListMutation
} = personalPlaylistApi;

export const studioPlaylistApi = createApi({
	reducerPath: 'studioPlaylistApi',
	tagTypes: ['studioPlaylist'],
	baseQuery: fetchBaseQuery({
		baseUrl: `${import.meta.env.VITE_APP_API_URL}/studio-playlist/`,
		prepareHeaders: (headers, { getState }) => {
			const token = getState()?.authState?.userInfo?.token;
			if (token) {
				headers.set('Authorization', `Bearer ${token}`);
			}
			return headers;
		}
	}),
	endpoints: builder => ({
		// Mutation for creating a playlist
		createStudioPlayList: builder.mutation({
			query: payload => ({
				url: 'create',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['studioPlaylist'] // Invalidate to refetch playlist list
		}),

		// Query for fetching all playlists
		getStudioPlayLists: builder.query({
			query: () => ({
				url: 'all',
				method: 'GET'
			}),
			providesTags: ['studioPlaylist'] 
		}),

		// Query for fetching a playlist by ID
		getStudioPlayListById: builder.query({
			query: id => ({
				url: `${id}`,
				method: 'GET'
			}),
			providesTags: (result, error, id) => [{ type: 'studioPlaylist', id }] // Dynamic tag based on ID
		}),
		// Query for fetching a playlist by ID
		getVideoOfStudioPlayListById: builder.query({
			query: ({ playlistId, id }) => ({
				url: `${playlistId}/${id}`,
				method: 'GET'
			}),
			providesTags: (result, error, id) => [{ type: 'studioPlaylist', id }] // Dynamic tag based on ID
		}),

		// Mutation for deleting a playlist
		deleteStudioPlayList: builder.mutation({
			query: id => ({
				url: `${id}`,
				method: 'DELETE'
			}),
			invalidatesTags: ['studioPlaylist'] // Invalidate all playlists
		}),

		// Mutation for updating a playlist
		patchStudioPlayList: builder.mutation({
			query: ({ id, payload }) => ({
				url: `${id}`,
				method: 'PATCH',
				body: payload
			}),
			invalidatesTags: (result, error, { id }) => [{ type: 'studioPlaylist', id }] // Invalidate specific playlist
		}),
		addToStudioPlayList: builder.mutation({
			query: payload => ({
				url: 'add',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['studioPlaylist']
		})
	})
});

// Export hooks for the API
export const {
	useCreateStudioPlayListMutation,
	useGetStudioPlayListsQuery,
	useGetStudioPlayListByIdQuery,
	useGetVideoOfStudioPlayListByIdQuery,
	useDeleteStudioPlayListMutation,
	usePatchStudioPlayListMutation,
	useAddToStudioPlayListMutation
} = studioPlaylistApi;

export const combinePlaylistApi = createApi({
	reducerPath: 'combinePlaylistApi',
	baseQuery: fetchBaseQuery({
		baseUrl: `${import.meta.env.VITE_APP_API_URL}/`,
		prepareHeaders: (headers, { getState }) => {
			if (getState()?.authState?.userToken) {
				headers.set('Authorization', 'Bearer ' + getState()?.authState?.userToken);
			}
			return headers;
		}
	}),
	endpoints: builder => ({
		getCombinedVideosById: builder.query({
			query: id => `combined/videos/${id}`
		})
	})
});

export const { useGetCombinedVideosByIdQuery } = combinePlaylistApi;
export const videoListApi = createApi({
	tagTypes: ['videoList'],
	reducerPath: 'videoListApi',
	baseQuery: fetchBaseQuery({
		baseUrl: `${import.meta.env.VITE_APP_API_URL}/`
		// prepareHeaders: (headers, { getState }) => {
		//   if (getState()?.authState?.userToken) {
		//     headers.set("Authorization", "Bearer " + getState()?.authState?.userToken);
		//   }
		//   return headers;
		// },
	}),
	endpoints: builder => ({
		getVideosList: builder.query({
			query: () => ({
				url: `video/fetch`,
				method: 'GET',
				cache: 'no-store'
			}),
			onQueryStarted: async (arg, { queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled;
					
				} catch (err) {
					console.error('Query Failed:', err);
				}
			},
			providesTags: ['videoList']
		}),
		dropDownPersonalList: builder.query({
			query: userId => ({
				url: `personalvideo/fetch/all/${userId}`,
				method: 'GET'
			}),
			providesTags: ['videoList']
		}),
		dropDownStudioList: builder.query({
			query: userId => ({
				url: `studiovideo/fetch/all/${userId}`,
				method: 'GET'
			}),
			providesTags: ['videoList']
		})
	})
});
export const { useGetVideosListQuery, useDropDownPersonalListQuery, useDropDownStudioListQuery } = videoListApi;

export const wishlistApi = createApi({
	tagTypes: ['wishlist'],
	reducerPath: 'wishlistApi',
	baseQuery: fetchBaseQuery({
		baseUrl: `${import.meta.env.VITE_APP_API_URL}/`,
		prepareHeaders: (headers, { getState }) => {
			if (getState()?.authState?.userToken) {
				headers.set('Authorization', 'Bearer ' + getState()?.authState?.userToken);
			}
			return headers;
		},
		credentials: 'include'
	}),
	endpoints: builder => ({
		saveWishlist: builder.mutation({
			query: payload => ({
				url: 'save/Wishlist/video',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['wishlist']
		}),
		unSaveWishlist: builder.mutation({
			query: payload => ({
				url: 'unsave/Wishlist/video',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['wishlist']
		}),
		personalWishlist: builder.mutation({
			query: payload => ({
				url: 'personalvideo/wishlist',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['wishlist']
		}),
		featuredWishlist: builder.mutation({
			query: payload => ({
				url: 'featurevideo/wishlist',
				method: 'POST',
				body: payload
			}),
			invalidatesTags: ['wishlist']
		})
	})
});
export const {
	useSaveWishlistMutation,
	useUnSaveWishlistMutation,
	usePersonalWishlistMutation,
	useFeaturedWishlistMutation
} = wishlistApi;
